import React from 'react'
import { Box, styled } from '@mui/material'
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom'

const Shell = styled(Box)({
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--ds-canvas)',
  padding: '40px 24px',
})

const Card = styled(Box)({
  maxWidth: 480,
  width: '100%',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

const Icon = styled('div')({
  fontSize: 52,
  marginBottom: 20,
  userSelect: 'none',
})

const Title = styled('h1')({
  margin: '0 0 10px',
  fontSize: 'clamp(20px, 3.5vw, 24px)',
  fontWeight: 740,
  letterSpacing: '-0.03em',
  color: 'var(--ds-ink)',
})

const Body = styled('p')({
  margin: '0 0 28px',
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--ds-ink-subtle)',
  maxWidth: 340,
})

const Detail = styled('code')({
  display: 'block',
  marginBottom: 28,
  padding: '10px 14px',
  borderRadius: 'var(--ds-radius-md)',
  background: 'var(--ds-hover)',
  fontSize: 11.5,
  fontFamily: 'var(--ds-font-mono)',
  color: 'var(--ds-ink-muted)',
  textAlign: 'left',
  maxWidth: '100%',
  overflowX: 'auto',
  wordBreak: 'break-all',
})

const Actions = styled(Box)({
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
  justifyContent: 'center',
})

const Btn = styled('button', {
  shouldForwardProp: (p) => p !== '$primary',
})<{ $primary?: boolean }>(({ $primary }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  height: 40,
  padding: '0 22px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 13.5,
  fontWeight: 640,
  cursor: 'pointer',
  transition: 'opacity 120ms ease, box-shadow 120ms ease',
  ...($primary
    ? {
        background: 'var(--ds-ink)',
        color: 'var(--ds-inverse-ink)',
        '&:hover': { opacity: 0.85 },
      }
    : {
        boxShadow: 'inset 0 0 0 1.5px var(--ds-border-strong)',
        color: 'var(--ds-ink)',
        '&:hover': { boxShadow: 'inset 0 0 0 1.5px var(--ds-ink-muted)' },
      }),
}))

/**
 * `errorElement` for the root router — shown when React Router itself throws
 * (bad loader, chunk load failure, missing route, etc.).  Uses
 * `useRouteError()` so we can distinguish 404-style route errors from
 * unexpected crashes and tailor the message accordingly.
 */
export function ErrorPage() {
  const error = useRouteError()
  const navigate = useNavigate()

  let title = 'Something went wrong'
  let body = 'An unexpected error occurred. Try reloading the page — if it keeps happening, the team has been notified.'
  let detail: string | null = null

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = 'Page not found'
      body = "The page you're looking for doesn't exist or has been moved."
    } else if (error.status === 401 || error.status === 403) {
      title = "You don't have access"
      body = 'Sign in or check with the owner to get access to this page.'
    } else {
      title = `Error ${error.status}`
      body = error.statusText || body
    }
  } else if (error instanceof Error) {
    detail = error.message
  }

  return (
    <Shell>
      <Card>
        <Icon>{isRouteErrorResponse(error) && error.status === 404 ? '🔍' : '⚡'}</Icon>
        <Title>{title}</Title>
        <Body>{body}</Body>
        {detail && <Detail>{detail}</Detail>}
        <Actions>
          <Btn onClick={() => window.location.reload()}>Reload</Btn>
          <Btn $primary onClick={() => navigate('/')}>Go home</Btn>
        </Actions>
      </Card>
    </Shell>
  )
}
